import { useState, useRef, useEffect } from "react";
import { Globe2, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Compact language picker used on Landing / Auth screens. Same choices
 * as Settings > Language, but a dropdown rather than a full section.
 * Persists via `useLanguage()`, so the choice survives the OAuth /
 * signup round-trip and the whole app reloads in the picked language.
 */
export default function LandingLanguagePicker({ testId = "landing-lang-picker" }) {
  const { language, setLanguage, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const active = languages.find((l) => l.code === language) || languages[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[var(--border-default)] text-[13px] text-[var(--text-secondary)] hover:text-[var(--accent-primary)] hover:border-[var(--accent-primary)] transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe2 size={14} />
        <span className="hidden sm:inline">{active.native}</span>
        <span className="sm:hidden font-mono uppercase text-[11px]">{active.code}</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-1 min-w-[160px] rounded-md border border-[var(--border-default)] bg-[var(--bg-primary)] shadow-lg z-50 overflow-hidden"
          data-testid={`${testId}-menu`}
        >
          {languages.map((l) => (
            <button
              key={l.code}
              role="option"
              aria-selected={l.code === language}
              onClick={() => { setLanguage(l.code); setOpen(false); }}
              data-testid={`${testId}-opt-${l.code}`}
              className={`w-full text-left px-3 py-2 text-[13px] flex items-center justify-between transition-colors ${
                l.code === language
                  ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                  : "text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              }`}
            >
              <span>
                <span className="font-medium">{l.native}</span>
                {l.native !== l.label && <span className="ml-1.5 text-[11px] text-[var(--text-muted)]">{l.label}</span>}
              </span>
              {l.code === language && <Check size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
